<template>
    <div class="home-view">
        <ContentLayout
            home
            :image="heroImage"
            :height="640"
            :mobile-height="560"
            :title="$store.state.data.title"
            :subtitle="$store.state.data.subtitle"
        >
            <template v-slot:hero-actions>
                <v-btn
                    x-large
                    color="secondary"
                    class="primary--text font-weight-bold mr-3 mb-3"
                    :to="{ name: 'inventory' }"
                >
                    <v-icon left>mdi-car-multiple</v-icon>
                    Voir l'inventaire
                </v-btn>
                <v-btn
                    x-large
                    outlined
                    color="white"
                    class="mr-3 mb-3"
                    :to="{ name: 'services' }"
                >
                    <v-icon left>mdi-spray-bottle</v-icon>
                    Lavage d'auto
                </v-btn>
                <v-btn
                    x-large
                    text
                    color="white"
                    class="mb-3"
                    :href="'tel:' + $store.state.data.phone"
                >
                    <v-icon left>mdi-phone</v-icon>
                    {{ $store.state.data.phone }}
                </v-btn>
            </template>

        <v-container class="quick-actions-container">
            <v-row dense>
                <v-col cols="12" md="4" class="d-flex">
                    <v-card class="quick-action-card pa-5" elevation="10">
                        <div class="d-flex align-center mb-4">
                            <v-avatar color="primary" size="48" class="mr-4">
                                <v-icon color="secondary">mdi-car-multiple</v-icon>
                            </v-avatar>
                            <div>
                                <div class="title font-weight-bold">Inventaire</div>
                                <div class="caption grey--text text--darken-1">Véhicules disponibles</div>
                            </div>
                        </div>
                        <p class="body-2 grey--text text--darken-2 mb-4">
                            Parcourez les nouveautés et trouvez un véhicule prêt pour la route.
                        </p>
                        <v-btn text color="primary" class="px-0 font-weight-bold" :to="{ name: 'inventory' }">
                            Magasiner
                            <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4" class="d-flex">
                    <v-card class="quick-action-card pa-5" elevation="10">
                        <div class="d-flex align-center mb-4">
                            <v-avatar color="secondary" size="48" class="mr-4">
                                <v-icon color="primary">mdi-spray-bottle</v-icon>
                            </v-avatar>
                            <div>
                                <div class="title font-weight-bold">Lavage d'auto</div>
                                <div class="caption grey--text text--darken-1">Rapide, Silver ou Gold</div>
                            </div>
                        </div>
                        <p class="body-2 grey--text text--darken-2 mb-4">
                            Choisissez un forfait clair pour un nettoyage intérieur et extérieur.
                        </p>
                        <v-btn text color="primary" class="px-0 font-weight-bold" :to="{ name: 'services' }">
                            Voir les forfaits
                            <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4" class="d-flex">
                    <v-card class="quick-action-card pa-5" elevation="10">
                        <div class="d-flex align-center mb-4">
                            <v-avatar color="primary" size="48" class="mr-4">
                                <v-icon color="secondary">mdi-tools</v-icon>
                            </v-avatar>
                            <div>
                                <div class="title font-weight-bold">Mécanique</div>
                                <div class="caption grey--text text--darken-1">Partenaire CDM Écono</div>
                            </div>
                        </div>
                        <p class="body-2 grey--text text--darken-2 mb-4">
                            Pour les réparations et l'entretien, visitez notre partenaire.
                        </p>
                        <v-btn
                            text
                            color="primary"
                            class="px-0 font-weight-bold"
                            href="https://cdmecono.com"
                            target="_blank"
                            rel="noopener"
                        >
                            cdmecono.com
                            <v-icon right>mdi-open-in-new</v-icon>
                        </v-btn>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <section class="inventory-section">
            <v-container>
                <v-row align="end" class="mb-6">
                    <v-col cols="12" md="8">
                        <div class="overline secondary--text font-weight-bold mb-2">Nouveautés</div>
                        <h2 class="display-1 font-weight-bold mb-3">Derniers véhicules arrivés</h2>
                        <p class="body-1 grey--text text--darken-1 mb-0">
                            Un aperçu rapide des plus récents véhicules ajoutés à notre inventaire.
                        </p>
                    </v-col>
                    <v-col cols="12" md="4" class="text-md-right">
                        <v-btn
                            large
                            outlined
                            color="primary"
                            :to="{ name: 'inventory' }"
                        >
                            <v-icon left>mdi-car-multiple</v-icon>
                            Tout voir
                        </v-btn>
                    </v-col>
                </v-row>

                <div class="d-flex" v-if="!fetching && items.length > 0">
                    <v-slide-group multiple :show-arrows="$vuetify.breakpoint.smAndUp" class="inventory-slider">
                        <template v-slot:prev>
                            <v-icon x-large>mdi-chevron-left</v-icon>
                        </template>
                        <template v-slot:next>
                            <v-icon x-large>mdi-chevron-right</v-icon>
                        </template>
                        <v-slide-item v-for="(item, i) in items" :key="i">
                            <VehicleCard :item="item" :width="340" card-class="mx-2 my-2" />
                        </v-slide-item>
                    </v-slide-group>
                </div>
                <div class="text-center py-12" v-else-if="fetching">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        size="80"
                    ></v-progress-circular>
                </div>
                <v-alert
                    v-else
                    prominent
                    outlined
                    type="info"
                    color="primary"
                    class="mb-0"
                >
                    Aucun véhicule à afficher pour le moment.
                </v-alert>
            </v-container>
        </section>
        </ContentLayout>
    </div>
</template>

<script>
    import ContentLayout from "./../layouts/Content";
    import VehicleCard from "./../elements/VehicleCard";

    // Module
    export default {
        name: "home-view",
        components: {
            ContentLayout,
            VehicleCard
        },
        data: () => ({
            fetching: false,
            items: []
        }),
        computed: {
            heroImage() {
                return this.$store.state.data.home_header_img ? 
                    this.$store.state.data.home_header_img : require("./../../assets/img/hero.jpg");
            }
        },
        methods: {
            fetch() {
                //console.log("fetch");
                this.fetching = true;
                this._apiRequest("items/get-all", { latest: true }).then(response => {
                    if(!response.error) {
                        this.items = response.items;
                    } else {
                        this.$root.snackbar.show({
                            text: response.error,
                            icon: "mdi-alert-circle",
                            color: "error"
                        });
                    }
                }).finally(() => {
                    this.fetching = false;
                });
            }
        },
        activated() {
            if(this.items.length === 0) {
                this.fetch();
            }
        }
    };
</script>

<style scoped>
    .home-view {
        background: #f5f6f8;
    }

    .quick-actions-container {
        position: relative;
        z-index: 2;
        margin-top: -42px;
    }

    .quick-action-card {
        width: 100%;
        border-radius: 8px;
        border: 1px solid rgba(34, 34, 34, 0.08);
        transition: transform 180ms ease, box-shadow 180ms ease;
    }

    .quick-action-card:hover {
        transform: translateY(-4px);
    }

    .inventory-section {
        padding: 64px 0;
    }

    .inventory-slider {
        width: 100%;
    }

    /* touch hack */
    .v-slide-group__wrapper {
        touch-action: auto !important;
    }

    @media (max-width: 959px) {
        .quick-actions-container {
            margin-top: -28px;
        }
    }

    @media (max-width: 599px) {
        .inventory-section {
            padding: 44px 0;
        }
    }
</style>
